import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react'
import { cn } from '@/lib/utils'

interface Props {
  length?: number
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  disabled?: boolean
  error?: boolean
  success?: boolean
  autoFocus?: boolean
}

export function OTPInput({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled,
  error,
  success,
  autoFocus = true,
}: Props) {
  const inputs = useRef<(HTMLInputElement | null)[]>([])
  const digits = value.padEnd(length, ' ').slice(0, length).split('').map((c) => (c === ' ' ? '' : c))

  useEffect(() => {
    if (autoFocus) inputs.current[0]?.focus()
  }, [autoFocus])

  const setAt = (index: number, char: string) => {
    if (!/^\d?$/.test(char)) return
    const next = digits.slice()
    next[index] = char
    const joined = next.join('')
    onChange(joined)
    if (char && index < length - 1) inputs.current[index + 1]?.focus()
    if (joined.length === length && next.every((d) => d) && onComplete) {
      onComplete(joined)
    }
  }

  const handleKey = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      if (digits[index]) {
        setAt(index, '')
      } else if (index > 0) {
        const prev = digits.slice()
        prev[index - 1] = ''
        onChange(prev.join(''))
        inputs.current[index - 1]?.focus()
      }
    } else if (e.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus()
    else if (e.key === 'ArrowRight' && index < length - 1) inputs.current[index + 1]?.focus()
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!pasted) return
    onChange(pasted)
    if (pasted.length === length && onComplete) onComplete(pasted)
    inputs.current[Math.min(pasted.length, length - 1)]?.focus()
  }

  return (
    <div className="flex gap-2 justify-center" onPaste={handlePaste}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputs.current[index] = el
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[index] || ''}
          onChange={(e) => setAt(index, e.target.value.slice(-1))}
          onKeyDown={(e) => handleKey(index, e)}
          disabled={disabled}
          className={cn(
            'w-12 h-14 text-center text-2xl font-bold border-2 rounded-lg transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-[#0A192F] focus:border-[#0A192F]',
            error && 'border-red-500 bg-red-50',
            success && 'border-green-500 bg-green-50',
            !error && !success && 'border-slate-300',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        />
      ))}
    </div>
  )
}
